import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionSummary,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaBug, FaCalendarAlt, FaInfinity } from "react-icons/fa";
import { HiMiniShieldCheck, HiOutlineShieldExclamation } from "react-icons/hi2";
import { MdNotifications, MdNotificationsOff, MdSnooze } from "react-icons/md";
import { RiShipFill } from "react-icons/ri";
import { TbZzz, TbZzzOff } from "react-icons/tb";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { PuffLoader } from "react-spinners";
import { Id, ToastContainer, toast } from "react-toastify";
import {
  ExceptionRecordQueryResultsType,
  RecordingSessionQueryResultsGroupedType,
  RecordingSessionType,
  SnoozeIssueInput,
  useCreateEngineeringTicketMutation,
  useGetEngineeringTicketPlatformIntegrationsQuery,
  useGetExceptionRecordQuery,
  useGetIssueLazyQuery,
  useSearchUsingQueryQuery,
  useSnoozeIssueMutation,
  useToggleIssueFollowingStatusForUserMutation,
  useToggleIssueResolvedStatusMutation,
  useUpdateIssueMutation,
} from "../__generated__/graphql";
import client from "../apolloClient";
import CtaButton from "../components/Buttons/CtaButton";
import HoverMenuButton from "../components/Buttons/HoverMenuButton";
import ExceptionView from "../components/Exceptions/ExceptionView"; // Imported ExceptionView
import GroupedRecordingsTimeline from "../components/GroupedRecordingsTimeline";
import ApplicationPageWithCta from "../components/Layout/ApplicationPageWithCta";
import ExceptionsList from "../components/ListViews/ExceptionsList";
import LoadingAnimation from "../components/LoadingAnimation";
import CtaModal from "../components/Modal/CtaModal";
import RecordingSummary from "../components/RecordingSummary/RecordingSummary";
import RRWebPlayerWithTriage from "../components/RRWebPlayerWithTriage";
import { useAdmin } from "../contexts/AdminContext";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { WebPlayerProvider } from "../contexts/WebPlayerContext";
import useContainerWidth from "../hooks/useContainerWidth";
import useUnzippedEvents from "../hooks/useUnzippedEvents";
import JiraLogo from "../images/jiraLogo.png";
import LinearLogo from "../images/linearLogo.png";
import {
  DEVICE_TYPES,
  DOCS_DOMAIN,
  INTEGRATIONS,
  JIRA_PRIORITIES,
  LINEAR_PRIORITIES,
  SUPPORT_TICKET_INTEGRATIONS,
} from "../utils/constants";
import {
  AutocompleteOption,
  DeviceType,
  HighlightedSection,
  LogLevel,
} from "../utils/interfaces";
import WebSocketService from "../utils/websocket";


const DEFAULT_ITEMS_PER_PAGE = 10;
const PLAYER_HEIGHT_PX = 462;

client.clearStore(); // Clears cached data

interface ProviderStatus {
  id: string;
  name: string;
  project_id?: string;
}

const IssuePage: React.FC = () => {
  // Refs
  const rrwebPlayerRef = useRef<any>(null);
  const exceptionsListContainerRef = useRef<HTMLDivElement | null>(null); // Ref for the exceptions list container
  const toastIdRef = useRef<Id | null>(null);

  // Router hooks
  const { issueId } = useParams<{ issueId: string }>();
  const { search } = useLocation();
  const navigate = useNavigate();

  // State hooks
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const [issueTitle, setIssueTitle] = useState<string>("");
  const [issueDescription, setIssueDescription] = useState<string>("");
  const [issueTeam, setIssueTeam] = useState("");
  const [issueProject, setIssueProject] = useState("");
  const [issuePriority, setIssuePriority] = useState(0);
  const [issueStatus, setIssueStatus] = useState(1);
  const [recordings, setRecordings] = useState<
    RecordingSessionQueryResultsGroupedType[]
  >([]);
  const [selectedRecording, setSelectedRecording] =
    useState<RecordingSessionType | null>(null);
  const [selectedRecordings, setSelectedRecordings] =
    useState<RecordingSessionQueryResultsGroupedType | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [selectedDeviceRecordings, setSelectedDeviceRecordings] = useState<
    RecordingSessionType[] | null
  >(null);
  const [timeline, setTimeline] = useState<[number, number]>([0, 0]);
  const [initialTime, setInitialTime] = useState<number | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [highlightedSections, setHighlightedSections] = useState<
    HighlightedSection[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [exceptionsPage, setExceptionsPage] = useState(1);
  const [logSearch, setLogSearch] = useState("");
  const [logLevel, setLogLevel] = useState<LogLevel | null>(null);
  const [logType, setLogType] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const exceptionIdParam = searchParams.get("exceptionId"); // Get the exceptionId from the URL

  // Context hooks
  const { selectedCompany } = useCompany();
  const { user } = useAuth();
  const { isAdminEnabled } = useAdmin();

  // Custom hooks
  const {
    unzippedEvents,
    eventsError,
    loadingEvents,
    fetchUnzippedEvents,
    setUnzippedEvents,
    setTimelineEvents,
  } = useUnzippedEvents(selectedRecording?.id, rrwebPlayerRef);
  const { containerRef, containerWidth } = useContainerWidth();

  // Query parameters
  const queryParams = new URLSearchParams(search);
  const recordingIdParam = queryParams.get("recordingId");

  // Apollo queries and mutations
  const [getIssue, { data, loading, error, refetch }] = useGetIssueLazyQuery({
    variables: {
      issueId: issueId ? parseInt(issueId, 10) : 0,
      companyId: selectedCompany
        ? parseInt(selectedCompany.value, 10)
        : undefined,
    },
    notifyOnNetworkStatusChange: true,
  });

  const hasEngineeringTicket =
    !!data?.getIssue?.engineeringTicket?.platformLink;

  const { data: integrations } =
    useGetEngineeringTicketPlatformIntegrationsQuery({
      variables: {
        companyId: selectedCompany
          ? parseInt(selectedCompany.value, 10)
          : undefined,
      },
    });

  const {
    integrationData,
    provider,
    taskPriorities,
    providerProjects,
    providerTeams,
    providerInstalled,
  } = useMemo(() => {
    // 1. Grab the list (may be empty)
    const list = (
      integrations?.getEngineeringTicketPlatformIntegrations || []
    ).filter((item) => SUPPORT_TICKET_INTEGRATIONS.includes(item.provider));
    // 2. Pick JIRA first, or fallback to the first element
    const provider = data?.getIssue?.engineeringTicket?.provider;
    const chosen = list.find((i) => i.provider === provider) || list[0] || null;

    // 3. Derive provider (default to LINEAR if nothing found)
    const prov = chosen?.provider ?? INTEGRATIONS.LINEAR;

    // 4. Pick priorities based on provider
    const prios =
      prov === INTEGRATIONS.JIRA ? JIRA_PRIORITIES : LINEAR_PRIORITIES;

    // 5.  Pick data based on provider
    const providerProjects: AutocompleteOption[] = chosen?.projects || [];
    const providerTeams: AutocompleteOption[] = chosen?.teams || [];
    const providerInstalled = chosen?.installed;

    return {
      integrationData: chosen,
      provider: prov,
      taskPriorities: prios,
      providerProjects,
      providerTeams,
      providerInstalled,
    };
  }, [integrations, data?.getIssue?.engineeringTicket?.provider]);

  const providerStatuses: ProviderStatus[] = useMemo(() => {
    if (provider === INTEGRATIONS.LINEAR) {
      return (integrationData?.workflowStates || []).map(
        ({ status_id, name }: any) => ({ id: status_id, name }),
      );
    }

    const projects = integrationData?.projects;
    if (!issueProject || !projects) {
      return [];
    }

    const selected = projects.find(
      (item: any) => String(item.id) === issueProject,
    );
    const statuses = (integrationData?.workflowStates || []).filter(
      (item: any) => item.project_id === selected?.project_id,
    );
    return statuses;
  }, [
    integrationData?.workflowStates,
    integrationData?.projects,
    provider,
    issueProject,
  ]);

  const [createEngineeringTicket] = useCreateEngineeringTicketMutation();
  const [updateIssueMutation] = useUpdateIssueMutation();

  const {
    data: exceptions,
    previousData: previousExceptions,
    loading: exceptionsLoading,
  } = useSearchUsingQueryQuery({
    variables: {
      searchEntity: "exception_records",
      searchQuery: `issue__id == ${issueId}`,
      limit: DEFAULT_ITEMS_PER_PAGE,
      offset: (exceptionsPage - 1) * DEFAULT_ITEMS_PER_PAGE,
      companyId: selectedCompany ? parseInt(selectedCompany.value, 10) : 0,
      orderBy: "-timestamp_ms",
    },
  });

  const selectedExceptionId =
    exceptionIdParam || data?.getIssue?.exceptions?.[0]?.id || "";

  const { data: exceptionRecord, loading: exceptionRecordLoading } =
    useGetExceptionRecordQuery({
      variables: {
        id: selectedExceptionId,
        companyId: selectedCompany
          ? parseInt(selectedCompany.value, 10)
          : undefined,
      },
      skip: !selectedExceptionId,
    });

  // Exceptions Data
  const handleExceptionSelect = (exceptionId: string) =>
    navigate({
      search: `?exceptionId=${exceptionId}`,
    });

  useEffect(() => {
    if (selectedCompany || !isAdminEnabled) {
      getIssue(); // Execute query when selectedCompany changes
    }
  }, [isAdminEnabled, selectedCompany]);

  // Calculated values
  const calculateTotalPages = (
    totalItems: number,
    itemsPerPage: number,
  ): number => {
    return Math.ceil(totalItems / itemsPerPage);
  };

  const exceptionsData = exceptionsLoading ? previousExceptions : exceptions;

  const exceptionsTotalPages = calculateTotalPages(
    exceptionsData?.searchUsingQuery?.results?.totalItemsCount || 0,
    DEFAULT_ITEMS_PER_PAGE,
  );

  // Effects
  useEffect(() => {
    // Sets default if they are empty
    !issueTeam && setIssueTeam(integrationData?.defaultTeam || "");
    !issueProject && setIssueProject(integrationData?.defaultProject || "");
    !issuePriority && setIssuePriority(integrationData?.defaultPriority || 0);
    !issueStatus && setIssueStatus(Number(providerStatuses?.[0]?.id || 1));
  }, [integrationData]);

  useEffect(() => {
    if (data && data.getIssue) {
      setIssueTitle(data.getIssue.title!);
      setIssueDescription(data.getIssue.description || "");
      const engineeringTicket = data.getIssue.engineeringTicket;
      engineeringTicket?.team && setIssueTeam(engineeringTicket.team);
      engineeringTicket?.project && setIssueProject(engineeringTicket.project);
      engineeringTicket?.priority &&
        setIssuePriority(engineeringTicket.priority || 0);
      const status = Number(providerStatuses?.[0]?.id || 1);
      engineeringTicket?.status &&
        setIssueStatus(engineeringTicket.status || status);

      const allRecordings: RecordingSessionQueryResultsGroupedType[] =
        data.getIssue.triages.flatMap((triage) => [
          ...(triage.triageRecordingData.length
            ? [
                {
                  userId:
                    triage.triageRecordingData[0].recording?.metadata?.user,
                  recordings: triage.triageRecordingData.map(
                    (d) => d.recording,
                  ),
                },
              ]
            : []),
          ...triage.triageLogData.map((d: any) => ({
            userId: d.recording?.metadata?.user,
            recordings: [d.recording],
          })),
          ...triage.otherSimilarRecordings.map((d) => ({
            userId: d.recording?.metadata?.user,
            recordings: [d.recording],
          })),
        ]);

      if (!allRecordings.length) return;

      setRecordings(allRecordings);

      const selectedUser = searchParams.get("selectedUser");
      const recordingGroup =
        (selectedUser &&
          (allRecordings.find(
            (r) => r.userId === selectedUser,
          ) as RecordingSessionQueryResultsGroupedType)) ||
        allRecordings[0];
      const device =
        (searchParams.get("device") as DeviceType) ||
        selectedDevice ||
        DEVICE_TYPES.DESKTOP;
      setSelectedRecordings(recordingGroup);
      const deviceRecordings =
        device === DEVICE_TYPES.DESKTOP
          ? recordingGroup.recordings.filter(
              (recording) => !recording.metadata?.device?.isMobile,
            )
          : recordingGroup.recordings.filter(
              (recording) => recording.metadata?.device?.isMobile,
            );
      setSelectedDeviceRecordings(deviceRecordings);
      const timestamp = searchParams.get("timestamp");
      const currentTimestamp = timestamp && parseInt(timestamp, 10);

      const recording =
        (currentTimestamp &&
          deviceRecordings.find(
            ({ startTimestamp, durationMs }) =>
              currentTimestamp >= startTimestamp &&
              currentTimestamp <= startTimestamp + durationMs,
          )) ||
        deviceRecordings[0];

      setSelectedRecording(recording);
      if (currentTimestamp) {
        setInitialTime(currentTimestamp);
      }
      setSelectedDevice(device);

      const firstGroup = allRecordings[0];
      const firstGroupRecordings = firstGroup.recordings;
      const firstRecording = firstGroupRecordings[0];
      const lastRecording =
        firstGroupRecordings[firstGroupRecordings.length - 1];

      setTimeline([
        firstRecording.startTimestamp,
        lastRecording.startTimestamp + lastRecording.durationMs,
      ]);

      const recordingData = data.getIssue.triages?.[0]?.triageRecordingData;

      if (!recordingData) return;

      // Process timeWindows to create HighlightedSections
      const sections: HighlightedSection[] = recordingData.flatMap(
        (data: any) =>
          data.timeWindows.map((window: any) => ({
            startTime: data.recording.startTimestamp + window.timestampStart,
            endTime: data.recording.startTimestamp + window.timestampEnd,
            text: 'Marked as "Error" Segment',
            color: "#fcd34d",
          })),
      );

      setHighlightedSections(sections);
    }
  }, [data, recordingIdParam, navigate]);

  // Notify user for status update
  useEffect(() => {
    const handleMessage = (message: MessageEvent) => {
      const data = JSON.parse(message.data);
      if (
        data?.message === "statusUpdate" &&
        (!toastIdRef.current || !toast.isActive(toastIdRef.current))
      ) {
        toastIdRef.current = toast(
          <div className='flex gap-2 text-sm font-medium items-end'>
            <div className='flex-1'>
              Please Refresh the page to get the real Other Similar Recordings
            </div>
            <button
              className='h-8 px-2 shadow rounded-md border'
              onClick={() => refetch()}
            >
              Refresh
            </button>
          </div>,
          { type: "info", className: "w-96" },
        );
      }
    };

    WebSocketService.subscribe(handleMessage);

    return () => {
      WebSocketService.unsubscribe(handleMessage);
    };
  }, [refetch]);

  // Updated toast
  useEffect(() => {
    if (toastIdRef.current) {
      if (loading) {
        toast.update(toastIdRef.current, {
          render: (
            <div className='flex gap-2 text-sm font-medium items-center'>
              <div className='flex-1'>Refreshing data...</div>
              <div className='flex items-center gap-2'>
                <PuffLoader color='#4A90E2' size={24} />
              </div>
            </div>
          ),
          type: "info",
          autoClose: false,
        });
      } else {
        toast.update(toastIdRef.current, {
          render: (
            <div className='text-sm font-medium'>Data has been updated!</div>
          ),
          type: "success",
          autoClose: 3000,
        });
        toastIdRef.current = null;
      }
    }
  }, [loading, toastIdRef]);

  // Event handlers
  const handleSelectRecording = useCallback(
    (recording: RecordingSessionType, fetchOnly?: boolean) => {
      if (fetchOnly) {
        fetchUnzippedEvents({
          variables: {
            id: recording.id,
            ...(selectedCompany && {
              companyId: parseInt(selectedCompany.value, 10),
            }),
          },
        });
        return;
      }
      if (selectedRecording?.id === recording.id) {
        // Same recording selected, seek back to the start and keep playing
        rrwebPlayerRef.current?.resetPlayer();
      } else {
        setUnzippedEvents(null); // Reset events to force RRWebPlayer re-initialization
        setTimelineEvents([]);
        setSelectedRecording(recording);
      }
    },
    [
      fetchUnzippedEvents,
      selectedCompany,
      selectedRecording?.id,
      setTimelineEvents,
      setUnzippedEvents,
    ],
  );

  const handlePageChangExceptions = (newPage: number) => {
    setExceptionsPage(newPage);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const resetFields = () => {
    setIssueTitle(data?.getIssue?.title || "");
    setIssueDescription(data?.getIssue?.description || "");
    setIssueTeam(
      data?.getIssue?.engineeringTicket?.team ||
        integrationData?.defaultTeam ||
        "",
    );
    setIssueProject(
      data?.getIssue?.engineeringTicket?.project ||
        integrationData?.defaultProject ||
        "",
    );
    setIssuePriority(
      data?.getIssue?.engineeringTicket?.priority ||
        integrationData?.defaultPriority ||
        0,
    );
    setIssueStatus(data?.getIssue?.engineeringTicket?.status || 1);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetFields();
  };

  const handleCloseCreateTicketModal = () => {
    setIsCreateTicketModalOpen(false);
    resetFields();
  };

  const isUserFollowingIssue = useMemo(() => {
    if (!user || !data || !data.getIssue || !data.getIssue.followers) {
      return false;
    }
    return data.getIssue.followers.some(
      (follower) => follower.email === user.email,
    );
  }, [user, data]);

  const isIssueResolved = useMemo(() => {
    if (!user || !data || !data.getIssue || !data.getIssue.isResolved) {
      return false;
    }
    return data.getIssue.isResolved;
  }, [user, data]);

  const [toggleFollowIssueMutation] =
    useToggleIssueFollowingStatusForUserMutation();

  const [toggleResolvedStatusMutation] = useToggleIssueResolvedStatusMutation({
    client,
  });

  const [updateIssueSnoozeStatus] = useSnoozeIssueMutation();

  const handleFollowToggle = async () => {
    if (!data?.getIssue?.id) return;
    await toggleFollowIssueMutation({
      variables: {
        input: {
          issueId: Number(data.getIssue.id),
          wantsToFollow: !isUserFollowingIssue,
        },
        companyId: selectedCompany
          ? parseInt(selectedCompany.value, 10)
          : undefined,
      },
    });

    toast(
      <div className='text-sm font-medium' data-testid='followingToast'>
        {isUserFollowingIssue ? "Not Following" : "Following"}
      </div>,
      {
        type: "success",
        autoClose: 3000,
      },
    );

    // Refetch the issue data to update the UI
    refetch();
  };

  const handleResolvedToggle = async () => {
    if (!data?.getIssue?.id) return;
    await toggleResolvedStatusMutation({
      variables: {
        input: {
          issueId: Number(data.getIssue.id),
          setResolvedStateTo: !isIssueResolved,
        },
        companyId: selectedCompany
          ? parseInt(selectedCompany.value, 10)
          : undefined,
      },
    });

    toast(
      <div className='text-sm font-medium' data-testid='resolveToast'>
        {isIssueResolved ? "Unresolved" : "Resolved"}
      </div>,
      {
        type: "success",
        autoClose: 3000,
      },
    );

    // Refetch the issue data to update the UI
    refetch();
  };

  const handleUpdateSnoozeMode = async (
    snoozeMode: "NewRelease" | "Indefinite" | "Until" | "Disabled",
    snoozedUntil?: string, // ISO string for 'Until' mode
  ) => {
    const issueId = data?.getIssue?.id;
    if (!issueId) return;

    console.log(`Snooze mode: ${snoozeMode}`);

    const input: SnoozeIssueInput = {
      issueId: Number(issueId),
      snoozedMode: snoozeMode,
      snoozedPermanently: snoozeMode === "Indefinite",
      ...(snoozeMode === "Until" && snoozedUntil ? { snoozedUntil } : {}),
    };

    await updateIssueSnoozeStatus({
      variables: {
        input,
        companyId: selectedCompany
          ? parseInt(selectedCompany.value, 10)
          : undefined,
      },
    });

    const snoozedText = getSnoozedText(snoozeMode, snoozedUntil);

    toast(
      <div className='text-sm font-medium' data-testid='snoozeToast'>
        {snoozedText ? `Snoozed until ${snoozedText}` : "Unsnoozed"}
      </div>,
      {
        type: "success",
        autoClose: 3000,
      },
    );

    // Refetch the issue data to update the UI
    refetch();
  };

  const handleCreateTicket = async () => {
    if (!issueTitle.trim()) {
      console.error("'Ticket Name' field is required.");
      return;
    }

    try {
      // Execute the createTicket mutation
      await createEngineeringTicket({
        variables: {
          input: {
            issueId: data?.getIssue?.id || "",
            title: issueTitle.trim(),
            description: issueDescription.trim(),
            team: issueTeam,
            project: issueProject,
            priority: issuePriority,
          },
          companyId: selectedCompany
            ? parseInt(selectedCompany.value, 10)
            : undefined,
        },
      });

      handleCloseCreateTicketModal();

      // Re-fetch issue data
      refetch();
    } catch (error) {
      console.error("Error creating ticket:", error);
    }
  };

  const handleUpdateIssue = async () => {
    if (!issueTitle.trim() || !issueDescription.trim()) {
      console.error("Both 'Issue Name' and 'Description' fields are required.");
      return;
    }

    try {
      // Execute the updateIssue mutation
      await updateIssueMutation({
        variables: {
          input: {
            id: data?.getIssue?.id,
            title: issueTitle.trim(),
            description: issueDescription.trim(),
          },
          teamId: issueTeam,
          projectId: issueProject,
          priority: issuePriority,
          status: issueStatus,
          companyId: selectedCompany
            ? parseInt(selectedCompany.value, 10)
            : undefined,
        },
      });

      handleCloseModal();

      // Re-fetch issue data
      refetch();
    } catch (error) {
      console.error("Error updating issue:", error);
    }
  };

  // Early returns after all hooks
  if ((loading && !toastIdRef) || (!data && !error))
    return <LoadingAnimation text='Loading issue details...' />;
  if (error) return <div>Error loading issue details</div>;

  // Variables dependent on data
  const pageName = `Issues > #${issueId}: ${data?.getIssue?.title}`;

  const specificDateSubMenu = (
    <div className='m-6'>
      <div className='flex flex-col items-center'>
        <div className='flex items-start space-x-4'>
          {" "}
          {/* Flexbox for date and time pickers */}
          <DatePicker
            showTimeSelect
            dateFormat='MMMM d, yyyy h:mm aa'
            selected={selectedDate}
            onChange={(date: Date | null) => {
              setSelectedDate(date);
              // if (date) {
              //   handleUpdateSnoozeMode("Until", date.toISOString());
              // }
            }}
            timeIntervals={15}
            // timeCaption='Time'
            inline
            calendarClassName='react-datepicker-custom' // Adding a custom class for styling
          />
        </div>

        {/* Additional Snooze Button */}
        <button
          onClick={() => {
            if (selectedDate) {
              handleUpdateSnoozeMode("Until", selectedDate.toISOString());
            }
          }}
          className='mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75'
        >
          Snooze
        </button>
      </div>
    </div>
  );

  const snoozeIcon = (() => {
    switch (data?.getIssue?.snoozedMode) {
      case "NewRelease":
        return <RiShipFill />;
      case "Until":
        return <FaCalendarAlt />;
      case "Indefinite":
        return <FaInfinity />;
      case "Disabled":
      default:
        return <TbZzz />;
    }
  })();

  const getSnoozedText = (
    snoozedMode?: string,
    snoozedUntil?: string | null,
  ) => {
    switch (snoozedMode) {
      case "NewRelease":
        return "Next Release";
      case "Until":
        return snoozedUntil;
      case "Indefinite":
        return "End of Time";
      case "Disabled":
      default:
        return "";
    }
  };

  const defaultContent = "-";

  // TODO Move Triage calculated data to the backend - SAI-848
  const occurrence = data?.getIssue;

  const occurrenceData = [
    {
      title: "Change/24h",
      content: occurrence?.change24h || defaultContent,
    },
    {
      title: "Last Seen",
      content: occurrence?.lastSeen
        ? DateTime.fromISO(occurrence.lastSeen, {
            zone: "utc",
          }).toRelative()
        : defaultContent,
    },
    {
      title: "Severity",
      content: occurrence?.severity || defaultContent,
    },
    {
      title: "Users Affected",
      content: occurrence?.affectedPercentageUsers || defaultContent,
    },
    {
      title: "Devices",
      content:
        occurrence?.devices?.map(
          (device, index) =>
            device && (
              <div key={index}>
                {device.device?.brand || "Unknown"} {device.device?.type || ""}
              </div>
            ),
        ) || defaultContent,
    },
  ];

  const handleRecordingGroupChange = (
    recordingGroup: RecordingSessionQueryResultsGroupedType,
  ) => {
    setSelectedRecordings(recordingGroup);
    const deviceRecordings = recordingGroup.recordings.filter(
      selectedDevice === "Mobile"
        ? (r) => r.metadata?.device?.isMobile
        : (r) => r.metadata?.device?.isDesktop,
    );
    setSelectedDeviceRecordings(deviceRecordings);
    handleSelectRecording(deviceRecordings[0]);

    const params = new URLSearchParams(searchParams);
    params.set("selectedUser", recordingGroup.userId);
    setSearchParams(params);
  };

  const currentRecordingIndex = recordings.findIndex(
    ({ userId }) => userId === selectedRecordings?.userId,
  );
  const isFirstRecording = currentRecordingIndex === 0;
  const isLastRecording = currentRecordingIndex === recordings.length - 1;

  const handleBackwardRecording = () => {
    handleRecordingGroupChange(recordings[currentRecordingIndex - 1]);
  };

  const handleForwardRecording = () => {
    handleRecordingGroupChange(recordings[currentRecordingIndex + 1]);
  };

  const backwardRecordingDisabled = isFirstRecording;
  const forwardRecordingDisabled = isLastRecording;

  //  TODO - split this into separate component per engineering ticket integration
  const createTicketFields: JSX.Element[] = [
    ...(providerTeams && providerTeams.length > 0
      ? [
          <Autocomplete
            key='Team'
            options={providerTeams}
            getOptionLabel={(option) => option.name}
            value={providerTeams.find(({ id }) => id === issueTeam) || null}
            onChange={(e, v) => setIssueTeam(v?.id || "")}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            renderInput={(params) => (
              <TextField {...params} label='Team' variant='outlined' />
            )}
            data-testid='teamInput'
          />,
        ]
      : []),
    <Autocomplete
      key='Project'
      disableClearable={provider == INTEGRATIONS.JIRA}
      options={providerProjects}
      getOptionLabel={(option) => option.name}
      value={providerProjects.find(({ id }) => id == issueProject) || null}
      onChange={(event, value) => setIssueProject(value?.id || "")}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField {...params} label='Project' variant='outlined' />
      )}
      data-testid='projectInput'
    />,
    <Autocomplete
      key='Priority'
      disableClearable
      options={taskPriorities}
      getOptionLabel={(option) => option.name} // Maps `name` for display
      value={taskPriorities.find(({ value }) => value == issuePriority)}
      onChange={(event, newValue) => setIssuePriority(newValue?.value || 0)}
      isOptionEqualToValue={(option, value) => option.value === value.value} // Compares `value`
      renderInput={(params) => (
        <TextField {...params} label='Priority' variant='outlined' />
      )}
      data-testid='priorityInput'
    />,
  ];

  const updateTicketFields: JSX.Element[] = [
    ...createTicketFields,
    <Autocomplete
      key='Status'
      disableClearable
      options={providerStatuses}
      getOptionLabel={(option) => option.name} // Maps `name` for display
      value={providerStatuses.find(({ id }) => id == String(issueStatus))}
      onChange={(event, newValue) => setIssueStatus(Number(newValue?.id) || 0)}
      isOptionEqualToValue={(option, value) => option.id === value?.id} // Compares `value`
      renderInput={(params) => (
        <TextField {...params} label='Status' variant='outlined' />
      )}
      data-testid='statusInput'
    />,
  ];

  const exceptionsList =
    (exceptionsData?.searchUsingQuery as ExceptionRecordQueryResultsType)
      ?.results?.items || [];

  return (
    <ApplicationPageWithCta
      pageName={pageName}
      helpLink={DOCS_DOMAIN}
      helpTooltip='Click here to learn about Issues.'
      CtaButton={
        <div className='flex gap-2'>
          {providerInstalled && !hasEngineeringTicket && (
            <button
              className='h-8 w-64 flex items-center bg-white text-black px-4 py-2 shadow rounded-md gap-1 text-sm font-medium'
              onClick={() => setIsCreateTicketModalOpen(true)}
              data-testid='createEngineeringTicket'
            >
              <img
                src={provider === INTEGRATIONS.JIRA ? JiraLogo : LinearLogo}
                width={24}
                alt='Linear Logo'
              />
              Create Engineering Ticket
            </button>
          )}
          <CtaButton
            titlePrimary='Edit Issue'
            icon={<FaBug />}
            bgPrimaryClass='bg-blue-500'
            textPrimaryClass='text-white'
            bgPrimaryHoverClass='hover:bg-blue-600'
            textPrimaryHoverClass='hover:text-white'
            onClick={handleOpenModal}
            showOutlineHoverPrimary={false}
            additionalClassNames='!px-2.5 h-8 rounded-md text-sm leading-6 font-medium shadow-md'
            additionalWidth='4px'
            startIcon
            dataTestId='editIssueButton'
          />
        </div>
      }
    >
      <div className='flex justify-between items-center pb-4'>
        <span className='font-medium' data-testid='issueDescription'>
          {data?.getIssue?.description}
        </span>
        <div className='flex items-center space-x-4'>
          <div className='flex items-center justify-end space-x-4'>
            <CtaButton
              titlePrimary='Not Following'
              titleSecondary='Following'
              bgPrimaryClass='bg-amber-100'
              bgSecondaryClass='bg-amber-300'
              bgPrimaryHoverClass='bg-amber-100'
              bgSecondaryHoverClass='bg-amber-300'
              textPrimaryClass='font-semibold text-slate-400'
              textSecondaryClass='font-bold text-slate-700'
              textPrimaryHoverClass='font-semibold text-slate-400'
              textSecondaryHoverClass='font-bold text-slate-700'
              showOutlineHoverPrimary={false}
              showOutlineHoverSecondary={false}
              iconPrimary={<MdNotificationsOff />}
              iconSecondary={<MdNotifications />}
              isPrimary={!isUserFollowingIssue}
              onClick={handleFollowToggle}
              additionalClassNames='!px-2.5 h-8 rounded-md text-sm leading-6 font-medium shadow-md'
              dataTestId='following'
            />

            <HoverMenuButton
              titlePrimary='Snooze'
              titleSecondary='Snoozed'
              icon={snoozeIcon}
              items={[
                ...(data?.getIssue?.snoozedMode !== "Disabled"
                  ? [
                      {
                        title: `Snoozed until ${getSnoozedText(
                          data?.getIssue?.snoozedMode,
                          data?.getIssue?.snoozedUntilFormatted,
                        )}`,
                        key: "snoozedUntil",
                        onClick: () => {},
                        icon: <MdSnooze />,
                        hoveredNotSelectedClassName:
                          "text-slate-700 cursor-default border-b",
                        notHoveredNotSelectedClassName:
                          "bg-white text-slate-700 cursor-default border-b",
                      },
                    ]
                  : []),
                {
                  title: "Next release",
                  key: "nextRelease",
                  onClick: () => handleUpdateSnoozeMode("NewRelease"),
                  icon: <RiShipFill />,
                  selected: data?.getIssue?.snoozedMode === "NewRelease",
                  hoveredNotSelectedClassName:
                    "hover:bg-sky-200 hover:text-slate-700 hover:font-semibold",
                  hoveredSelectedClassName: "bg-blue-500 text-white font-bold",
                  notHoveredNotSelectedClassName: "bg-white text-slate-700",
                  notHoveredSelectedClassName:
                    "bg-blue-500 text-white font-bold",
                },
                {
                  title: "Specific Date/Time",
                  key: "specificDate",
                  icon: <FaCalendarAlt />,
                  selected: data?.getIssue?.snoozedMode === "Until",
                  hoveredNotSelectedClassName:
                    "hover:bg-sky-200 hover:text-slate-700 hover:font-semibold",
                  hoveredSelectedClassName: "bg-sky-400 text-white font-bold",
                  notHoveredNotSelectedClassName: "bg-white text-slate-700",
                  notHoveredSelectedClassName:
                    "bg-blue-500 text-white font-bold",
                  subMenu: specificDateSubMenu,
                  subMenuPosition: "left",
                },
                {
                  title: "End of Time",
                  key: "endOfTime",
                  onClick: () => handleUpdateSnoozeMode("Indefinite"),
                  icon: <FaInfinity />,
                  selected: data?.getIssue?.snoozedMode === "Indefinite",
                  hoveredNotSelectedClassName:
                    "hover:bg-sky-200 hover:text-slate-700 hover:font-semibold",
                  hoveredSelectedClassName: "bg-blue-500 text-white font-bold",
                  notHoveredNotSelectedClassName: "bg-white text-slate-700",
                  notHoveredSelectedClassName:
                    "bg-blue-500 text-white font-bold",
                },
                ...(data?.getIssue?.snoozedMode !== "Disabled"
                  ? [
                      {
                        title: "Unsnooze",
                        key: "unsnooze",
                        onClick: () => handleUpdateSnoozeMode("Disabled"),
                        icon: <TbZzzOff />,
                        selected: data?.getIssue?.snoozedMode === "Disabled",
                        hoveredNotSelectedClassName:
                          "hover:bg-sky-200 hover:text-slate-700 hover:font-semibold",
                        notHoveredNotSelectedClassName:
                          "bg-white text-slate-700",
                      },
                    ]
                  : []),
              ]}
              bgPrimaryClass='bg-slate-400'
              bgPrimaryHoverClass='bg-slate-400'
              bgSecondaryClass='bg-gray-500'
              bgSecondaryHoverClass='bg-gray-500'
              textPrimaryClass='text-white'
              textPrimaryHoverClass='text-white'
              textSecondaryClass='text-white'
              textSecondaryHoverClass='text-white'
              hideDelay={500}
              isPrimary={data?.getIssue?.snoozedMode === "Disabled"}
              additionalClassNames='!px-2.5 h-8 rounded-md text-sm leading-6 font-medium shadow-md'
              dataTestId='snooze'
            />

            <CtaButton
              titlePrimary='Unresolved'
              titleSecondary='Resolved'
              bgPrimaryClass='bg-green-100'
              bgSecondaryClass='bg-green-500'
              bgPrimaryHoverClass='bg-green-100'
              bgSecondaryHoverClass='bg-green-500'
              textPrimaryClass='font-semibold text-slate-800'
              textSecondaryClass='text-slate-50'
              textPrimaryHoverClass='font-semibold text-slate-800'
              textSecondaryHoverClass='text-slate-50'
              showOutlineHoverPrimary={false}
              showOutlineHoverSecondary={false}
              iconPrimary={<HiOutlineShieldExclamation size={20} />}
              iconSecondary={<HiMiniShieldCheck size={20} />}
              isPrimary={!isIssueResolved}
              onClick={handleResolvedToggle}
              additionalClassNames='!px-2.5 h-8 rounded-md text-sm leading-6 font-medium shadow-md'
              dataTestId='resolved'
            />
          </div>
        </div>
      </div>
      {isModalOpen && (
        <CtaModal
          title='Edit Issue'
          dataTestId='editIssueModal'
          closeModal={handleCloseModal}
          fields={[
            {
              label: "Issue Title",
              type: "input",
              defaultValue: issueTitle,
              placeholderText: "Enter a title for the issue",
              dataTestId: "issueTitle",
              onChange: (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => setIssueTitle(e.target.value),
            },
            {
              label: "Description",
              type: "textarea",
              defaultValue: issueDescription,
              placeholderText: "Issue description...",
              dataTestId: "issueDescription",
              onChange: (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => setIssueDescription(e.target.value),
            },
          ]}
          buttons={[
            {
              button: (
                <CtaButton
                  titlePrimary='Cancel'
                  bgPrimaryClass='bg-gray-300'
                  textPrimaryClass='text-secondary'
                  bgPrimaryHoverClass='hover:bg-gray-500'
                  textPrimaryHoverClass='hover:text-primary-dark'
                  outlinePrimaryHoverClass='hover:outline hover:outline-2 hover:outline-offset-0 hover:outline-gray-800'
                  additionalClassNames='w-full'
                  dataTestId='cancel'
                  onClick={handleCloseModal}
                />
              ),
              width: 50,
            },
            {
              button: (
                <CtaButton
                  titlePrimary='Save'
                  additionalClassNames='w-full font-semibold'
                  dataTestId='save'
                  onClick={handleUpdateIssue}
                />
              ),
              width: 50,
            },
          ]}
          additionalFields={
            providerInstalled && hasEngineeringTicket ? updateTicketFields : []
          }
        />
      )}

      {isCreateTicketModalOpen && (
        <CtaModal
          title='Create Engineering Ticket'
          closeModal={handleCloseCreateTicketModal}
          fields={[
            {
              label: "Ticket Title",
              type: "input",
              defaultValue: issueTitle,
              placeholderText: "Enter a title for the ticket",
              onChange: (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => setIssueTitle(e.target.value),
            },
            {
              label: "Description",
              type: "textarea",
              defaultValue: issueDescription,
              placeholderText: "Ticket description...",
              onChange: (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => setIssueDescription(e.target.value),
            },
          ]}
          buttons={[
            {
              button: (
                <CtaButton
                  titlePrimary='Cancel'
                  bgPrimaryClass='bg-gray-300'
                  textPrimaryClass='text-secondary'
                  bgPrimaryHoverClass='hover:bg-gray-500'
                  textPrimaryHoverClass='hover:text-primary-dark'
                  outlinePrimaryHoverClass='hover:outline hover:outline-2 hover:outline-offset-0 hover:outline-gray-800'
                  additionalClassNames='w-full'
                  dataTestId='cancel'
                  onClick={handleCloseCreateTicketModal}
                />
              ),
              width: 50,
            },
            {
              button: (
                <CtaButton
                  titlePrimary='Save'
                  additionalClassNames='w-full font-semibold'
                  dataTestId='save'
                  onClick={handleCreateTicket}
                />
              ),
              width: 50,
            },
          ]}
          additionalFields={createTicketFields}
          dataTestId='createEngineeringTicket'
        />
      )}

      {errorMessage && (
        <div className='error' data-testid='errorMessage'>
          {errorMessage}
        </div>
      )}
      {recordings.length ? (
        <div className='relative flex flex-col gap-4 flex-1'>
          <WebPlayerProvider>
            <div ref={containerRef}>
              <div className='h-full'>
                {selectedRecording && (
                  <RRWebPlayerWithTriage
                    id={selectedRecording.id}
                    ref={rrwebPlayerRef}
                    events={unzippedEvents}
                    highlightedSections={highlightedSections}
                    width={containerWidth - 16}
                    height={PLAYER_HEIGHT_PX}
                    containerWidth={containerWidth}
                    eventsError={eventsError}
                    loadingEvents={loadingEvents}
                    handleBackwardRecording={handleBackwardRecording}
                    handleForwardRecording={handleForwardRecording}
                    backwardRecordingDisabled={backwardRecordingDisabled}
                    forwardRecordingDisabled={forwardRecordingDisabled}
                    hideTriageButton
                    handleSelectRecording={handleSelectRecording}
                    selectedRecordings={selectedRecordings}
                    timeline={timeline}
                    selectedDevice={selectedDevice}
                    selectedDeviceRecordings={selectedDeviceRecordings}
                    onPlay={(device) => {
                      if (!selectedRecordings) return;
                      const deviceRecordings =
                        device === DEVICE_TYPES.DESKTOP
                          ? selectedRecordings.recordings.filter(
                              (recording: any) =>
                                !recording.metadata?.device?.isMobile,
                            )
                          : selectedRecordings.recordings.filter(
                              (recording: any) =>
                                recording.metadata?.device?.isMobile,
                            );
                      setSelectedDeviceRecordings(deviceRecordings);
                      handleSelectRecording(deviceRecordings[0]);
                      setSelectedDevice(device);
                    }}
                    selectedRecording={selectedRecording}
                    initialTime={initialTime}
                  />
                )}
              </div>
            </div>
            <RecordingSummary
              logLevel={logLevel}
              logSearch={logSearch}
              logType={logType}
              recordings={selectedDeviceRecordings}
              seekToTime={(time) => {
                if (rrwebPlayerRef.current) {
                  rrwebPlayerRef.current.seekToTime(time);
                }
              }}
              setLogLevel={setLogLevel}
              setLogSearch={setLogSearch}
              setLogType={setLogType}
            />
          </WebPlayerProvider>
          <div className='flex flex-col gap-3 text-xs pb-4'>
            <div className='font-bold text-sm' data-testid='similarRecordings'>
              Similar Recordings
            </div>
            {recordings.map(({ userId, recordings }) => (
              <div
                key={userId}
                className='flex gap-2 border-b border-slate-300 pb-3'
                data-testid='similarRecordingItem'
              >
                <p className='w-28 truncate font-medium' data-testid='userId'>
                  {userId}
                </p>
                <GroupedRecordingsTimeline
                  recordings={recordings}
                  selected={
                    selectedRecordings?.userId === userId && selectedDevice
                  }
                  timeline={timeline}
                  onPlay={(device) => {
                    const deviceRecordings =
                      device === DEVICE_TYPES.DESKTOP
                        ? recordings.filter(
                            (recording: any) =>
                              !recording.metadata?.device?.isMobile,
                          )
                        : recordings.filter(
                            (recording: any) =>
                              recording.metadata?.device?.isMobile,
                          );
                    setSelectedRecordings({ userId, recordings });
                    setSelectedDeviceRecordings(deviceRecordings);
                    handleSelectRecording(deviceRecordings[0]);
                    setSelectedDevice(device);

                    const params = new URLSearchParams(searchParams);
                    params.set("selectedUser", userId);
                    params.set("device", device);
                    setSearchParams(params);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className='relative flex gap-4 overflow-hidden'>
          <div
            className='flex flex-[5] flex-col max-h-full min-w-0'
            data-testid='exceptionDetailsContainer'
          >
            {exceptionRecordLoading ? (
              <LoadingAnimation text='Loading exception details...' />
            ) : (
              !!exceptionRecord?.getExceptionRecord && (
                // Display ExceptionView if there are no recordings
                <ExceptionView {...exceptionRecord.getExceptionRecord} />
              )
            )}
          </div>
          <div className='flex flex-[2] flex-col gap-2 max-h-full min-w-0'>
            <Accordion
              className='!shadow-md border border-gray-200 !rounded !m-0 flex flex-col p-2.5 overflow-auto min-h-14'
              defaultExpanded
              classes={{ expanded: "min-h-32" }}
              data-testid='occurrenceInfo'
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel1-content'
                id='panel1-header'
                className='text-sm font-semibold !min-h-8'
                classes={{ content: "!my-0" }}
              >
                Occurrence Info
              </AccordionSummary>
              <div
                className='grid grid-cols-2 gap-4 p-4 text-sm leading-[18px]'
                data-testid='itemsContainer'
              >
                {occurrenceData.map(({ title, content }) => (
                  <div
                    key={title}
                    className='border border-sky-200 rounded-md shadow-md p-2'
                  >
                    <div data-testid={title}>{title}</div>
                    <div className='font-bold' data-testid='content'>
                      {content}
                    </div>
                  </div>
                ))}
              </div>
            </Accordion>
            <Accordion
              ref={exceptionsListContainerRef}
              className='!shadow-md border border-gray-200 !rounded !m-0 flex flex-col p-2.5 overflow-auto'
              defaultExpanded
              data-testid='otherOccurrences'
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='panel2-content'
                id='panel2-header'
                className='text-sm font-semibold !min-h-8'
                classes={{ content: "!my-0" }}
              >
                Other Occurrences
              </AccordionSummary>
              <ExceptionsList
                containerRef={exceptionsListContainerRef}
                exceptions={exceptionsList}
                page={exceptionsPage}
                selectedException={selectedExceptionId}
                totalPages={exceptionsTotalPages}
                onPageChange={handlePageChangExceptions}
                onSelect={handleExceptionSelect}
              />
            </Accordion>
          </div>
        </div>
      )}
      <ToastContainer />
    </ApplicationPageWithCta>
  );
};

export default IssuePage;
